import { useCallback, useContext, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { TableActionKey, TableAction, TableActionsContext } from '../types';
import {
  BiContextProvidedValue,
  BiData,
  AddToGroupClickBiEvent,
  GiveBadgeAwardBiEvent,
  ClickCopyBiEvent,
  ClickSocialShareBiEvent,
  DeviationEditClickBiEvent,
  DeviationDeleteClickBiEvent,
  ItemClickBiEvent,
  UnlistItemOpenBiEvent,
  UnlistItemConfirmBiEvent,
  ScheduleOpenClickBiEvent,
  SellYourArtClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import { getDeviationResourceType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { getDeviationEntityId } from '@wix/da-shared-react/pkg/redux/normalizr/schemas/deviation';
import useDuperbrowseLink from '@wix/da-shared-react/pkg/Duperbrowse/hooks/useDuperbrowseLink';
import { useCopyDeviationLinkClick } from '@wix/da-shared-react/pkg/utils/hooks/useCopyLinkClick';
import {
  OnUnpublishCompleteAction,
  shareDeviationToPost,
} from '@wix/da-shared-react/pkg/redux/deviations/actionCreators';
import {
  pushAddToGroupModal,
  pushBoostModal,
  pushModal,
} from '@wix/da-shared-react/pkg/Modals/redux/actionCreators';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import DeviationMediaService from '@wix/da-shared-react/pkg/utils/DeviationMediaService';
import { useUnpublishFlow } from '@wix/da-shared-react/pkg/utils/hooks/useUnpublishFlow';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import ViewButton from '../ViewButton';
import EditButton from '../EditButton';
import BoostButton from '../BoostButton';
import CopyLinkButton from '../CopyLinkButton';
import SellButton from '../SellButton';
import OriginalStatusEdit from '../../StudioDeviationList/_partials/OriginalStatusEdit';
import {
  getActiveSection,
  getIsBoostSupported,
  getIsDraftsSection,
  getIsPublishedDataset,
  getIsUnpublishedDataset,
  getStudioParams,
  getTableLayout,
} from '../../../selectors/studio';
import { useEditDeviationClick } from '../../../hooks/useEditDeviationClick';
import { useReorderAction } from './useReorderAction';
import { useMoveToFolderActions } from './useMoveToFolderActions';
import { useSaleActions } from './useSaleActions';
import { useDreamupEditAction } from './useDreamupEditAction';
import { StudioSection, TableLayout } from '../../../types/store';
import { OriginalStatusType } from '../../../types/originals';
import { changeOriginalStatus } from '../../../actions/studio/originals';
import { BiContext } from '@wix/da-bi/pkg/BiLogger.context';
import { addNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import { NotificationType } from '@wix/da-shared-react/pkg/Notifications';

export function useTableActionsRegistry(
  context: TableActionsContext
): Record<TableActionKey, TableAction | null> {
  const { deviation, offset } = context;
  const viewAction = useViewAction(deviation);
  const editAction = useEditAction(deviation);
  const editAndPublishAction = useEditAndPublishAction(deviation);
  const deleteAction = useDeleteAction(context);
  const copyLinkAction = useCopyLinkAction(deviation);
  const boostAction = useBoostAction(deviation);
  const reorderAction = useReorderTableAction(deviation, offset);
  const moveToFolderAction = useMoveToFolderAction(deviation);
  const addToGroupAction = useAddToGroupAction(deviation);
  const shareAction = useShareAction(deviation);
  const dreamupEditAction = useDreamupEditTableAction(deviation);
  const scheduleAction = useScheduleAction(deviation);
  const unpublishAction = useUnpublishAction(deviation);
  const sellAction = useSellAction(deviation);
  const publishTierAction = usePublishTierAction(deviation);
  const originalStatusAction = useOriginalStatusAction(deviation);

  return useMemo(
    () => ({
      view: viewAction,
      edit: editAction,
      editAndPublish: editAndPublishAction,
      delete: deleteAction,
      copyLink: copyLinkAction,
      boost: boostAction,
      reorder: reorderAction,
      moveToFolder: moveToFolderAction,
      addToGroup: addToGroupAction,
      share: shareAction,
      dreamupEdit: dreamupEditAction,
      schedule: scheduleAction,
      unpublish: unpublishAction,
      sell: sellAction,
      publishTier: publishTierAction,
      originalStatus: originalStatusAction,
    }),
    [
      viewAction,
      editAction,
      editAndPublishAction,
      deleteAction,
      copyLinkAction,
      boostAction,
      reorderAction,
      moveToFolderAction,
      addToGroupAction,
      shareAction,
      dreamupEditAction,
      scheduleAction,
      unpublishAction,
      sellAction,
      publishTierAction,
      originalStatusAction,
    ]
  );
}

function useViewAction(deviation: PapiDeviation): TableAction {
  const { t } = useTranslation();
  const activeSection = useSelector(getActiveSection);
  const ref = useRef(null);
  const onViewClick = useDuperbrowseLink(getDeviationEntityId(deviation), ref, undefined, true);

  const showButton = activeSection !== StudioSection.PRODUCTS_ORIGINAL;

  return useMemo(
    () => ({
      key: 'view',
      display: 'both',
      renderButton: showButton
        ? (isMainCta: boolean) => (
            <ViewButton
              key="view-button"
              deviation={deviation}
              variant={isMainCta ? 'ghost' : 'icon'}
              biData={BiData<ItemClickBiEvent>({
                evid: 100,
                typeid: getDeviationResourceType(deviation),
                itemid: deviation.deviationId,
                sectionname: 'view_btn',
              })}
            />
          )
        : undefined,
      getDropdownItem: () => ({
        label: deviation.tier
          ? t('studio.table.actions.view.tier')
          : t('studio.table.actions.view'),
        biData: BiData<ItemClickBiEvent>({
          evid: 100,
          typeid: getDeviationResourceType(deviation),
          itemid: deviation.deviationId,
          sectionname: BiContextProvidedValue,
        }),
        onClick: onViewClick,
      }),
      dropdownGroupIndex: 1,
      dropdownOrder: 0,
    }),
    [t, deviation, onViewClick, showButton]
  );
}

export function useEditAction(deviation: PapiDeviation): TableAction {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { openEditModal } = useEditDeviationClick();

  return useMemo(
    () => ({
      key: 'edit',
      display: 'both',
      renderButton: (isMainCta: boolean) => (
        <EditButton
          key="edit-button"
          deviation={deviation}
          biData={BiData<DeviationEditClickBiEvent>({
            evid: 371,
            typeid: getDeviationResourceType(deviation),
            itemid: deviation.deviationId,
            sectionname: 'edit_btn',
          })}
          variant={isMobile ? 'text' : isMainCta ? 'ghost' : 'icon'}
        />
      ),
      getDropdownItem: () => ({
        label: t('studio.table.actions.edit'),
        onClick: () => openEditModal(deviation),
        biData: BiData<DeviationEditClickBiEvent>({
          evid: 371,
          typeid: getDeviationResourceType(deviation),
          itemid: deviation.deviationId,
          sectionname: BiContextProvidedValue,
        }),
      }),
      dropdownGroupIndex: 1,
      dropdownOrder: 0,
    }),
    [t, deviation, openEditModal, isMobile]
  );
}

function useEditAndPublishAction(deviation: PapiDeviation): TableAction {
  const { t } = useTranslation();
  const { onEditClick } = useEditDeviationClick(deviation);

  return useMemo(
    () => ({
      key: 'editAndPublish',
      display: 'both',
      renderButton: () => (
        <GhostButton
          key="edit-and-publish-button"
          type="button"
          variant="main"
          size="medium"
          onClick={onEditClick}
          biData={BiData<DeviationEditClickBiEvent>({
            evid: 371,
            typeid: getDeviationResourceType(deviation),
            itemid: deviation.deviationId,
            sectionname: 'edit_and_publish_btn',
          })}
        >
          {t('studio.products.original.editAndPublish')}
        </GhostButton>
      ),
      getDropdownItem: () => ({
        label: t('studio.products.original.editAndPublish'),
        onClick: onEditClick,
        biData: BiData<DeviationEditClickBiEvent>({
          evid: 371,
          typeid: getDeviationResourceType(deviation),
          itemid: deviation.deviationId,
          sectionname: BiContextProvidedValue,
        }),
      }),
      dropdownGroupIndex: 1,
      dropdownOrder: 0,
    }),
    [t, deviation, onEditClick]
  );
}

function useCopyLinkAction(deviation: PapiDeviation): TableAction | null {
  const { t } = useTranslation();
  const activeSection = useSelector(getActiveSection);
  const { contentType } = useSelector(getStudioParams);
  const { onCopyLinkClick } = useCopyDeviationLinkClick(deviation);

  const showButton =
    (activeSection !== StudioSection.PUBLISHED || contentType === 'posts') &&
    activeSection !== StudioSection.PRODUCTS_ORIGINAL;

  const biData = useMemo(
    () =>
      BiData<ClickCopyBiEvent>({
        evid: 357,
        itemid: deviation.deviationId,
        typeid: getDeviationResourceType(deviation),
        sectionname: BiContextProvidedValue,
      }),
    [deviation]
  );

  return useMemo(
    () => ({
      key: 'copyLink',
      display: 'both',
      renderButton: showButton
        ? () => (
            <CopyLinkButton
              key="copy-link-button"
              deviation={deviation}
              variant="icon"
              biData={biData}
            />
          )
        : undefined,
      getDropdownItem: () => ({
        label: t('studio.table.actions.copyLink'),
        onClick: onCopyLinkClick,
        biData,
      }),
      dropdownGroupIndex: 1,
      dropdownOrder: 1,
    }),
    [t, deviation, onCopyLinkClick, showButton, biData]
  );
}

export function useBoostAction(deviation: PapiDeviation): TableAction | null {
  const { t } = useTranslation();
  const { sectionname } = useContext(BiContext);
  const isMobile = useContext(MobileContext);
  const isBoostSupported = useSelector(getIsBoostSupported);
  const isGridLayout = useSelector(getTableLayout) === TableLayout.GRID;

  const dispatchPushBoostModal = useAction(pushBoostModal);
  const onClick = useCallback(() => {
    dispatchPushBoostModal(deviation, sectionname);
  }, [deviation, dispatchPushBoostModal, sectionname]);

  return useMemo(() => {
    if (!isBoostSupported) {
      return null;
    }

    return {
      key: 'boost',
      display: 'either',
      renderButton: () => (
        <BoostButton
          key="boost-button"
          deviation={deviation}
          variant={isMobile ? 'text' : 'icon'}
        />
      ),
      getDropdownItem: isGridLayout
        ? undefined
        : () => {
            // boost is disabled for tiers pending deletion
            const isBoostDisabled = deviation.tier?.state === 'pending_deletion';

            return {
              label: t('studio.table.actions.boost'),
              onClick,
              disabled: isBoostDisabled,
              biData: BiData<GiveBadgeAwardBiEvent>({
                evid: 320,
                sectionname: BiContextProvidedValue,
                itemid: deviation.deviationId,
                typeid: ResourceType.TIER,
                badge_type: 'boost',
                badge_typeid: 176,
              }),
            };
          },
      dropdownGroupIndex: 1,
      dropdownOrder: 0,
    };
  }, [t, deviation, onClick, isBoostSupported, isGridLayout, isMobile]);
}

function useReorderTableAction(deviation: PapiDeviation, offset: number): TableAction {
  const reorderAction = useReorderAction(deviation, offset);
  return useMemo(
    () => ({
      key: 'reorder',
      display: 'dropdown',
      getDropdownItem: () => reorderAction,
      dropdownGroupIndex: 0,
      dropdownOrder: 1,
    }),
    [reorderAction]
  );
}

function useMoveToFolderAction(deviation: PapiDeviation): TableAction {
  const moveToFolderActions = useMoveToFolderActions(deviation);
  return useMemo(
    () => ({
      key: 'moveToFolder',
      display: 'dropdown',
      getDropdownItem: () => moveToFolderActions,
      dropdownGroupIndex: 0,
      dropdownOrder: 1,
    }),
    [moveToFolderActions]
  );
}

function useDeleteAction(context: TableActionsContext): TableAction {
  const { t } = useTranslation();
  const { deviation, enactDeleteFlow } = context;
  const isDraftsSection = useSelector(getIsDraftsSection);
  // delete is disabled for tiers already pending deletion
  const isDisabled = deviation.tier?.state === 'pending_deletion';

  return useMemo(
    () => ({
      key: 'delete',
      display: 'dropdown',
      getDropdownItem: () => ({
        label: t(
          isDraftsSection ? 'studio.table.actions.deleteDraft' : 'studio.table.actions.delete'
        ),
        onClick: enactDeleteFlow,
        biData: BiData<DeviationDeleteClickBiEvent>({
          evid: 372,
          itemid: deviation.deviationId,
          typeid: getDeviationResourceType(deviation),
        }),
        disabled: isDisabled,
      }),
      dropdownGroupIndex: 2,
      dropdownOrder: 3,
    }),
    [t, deviation, enactDeleteFlow, isDraftsSection, isDisabled]
  );
}

function useAddToGroupAction(deviation: PapiDeviation): TableAction | null {
  const { t } = useTranslation();
  const dispatchAddToGroupModal = useAction(pushAddToGroupModal);
  const isPublishedDataset = useSelector(getIsPublishedDataset);

  return useMemo(() => {
    if (!isPublishedDataset) {
      return null;
    }

    return {
      key: 'addToGroup',
      display: 'dropdown',
      getDropdownItem: () => ({
        label: t('studio.table.actions.addToGroup'),
        biData: BiData<AddToGroupClickBiEvent>({
          evid: 340,
          deviationid: deviation.deviationId,
          sectionname: BiContextProvidedValue,
        }),
        onClick: () => {
          dispatchAddToGroupModal(deviation, {
            source_type: 'studio',
          });
        },
      }),
      dropdownGroupIndex: 0,
      dropdownOrder: 1,
    };
  }, [t, deviation, dispatchAddToGroupModal, isPublishedDataset]);
}

function useShareAction(deviation: PapiDeviation): TableAction | null {
  const { t } = useTranslation();
  const dispatchShareDeviationToPost = useAction(shareDeviationToPost);
  const isPublishedDataset = useSelector(getIsPublishedDataset);

  return useMemo(() => {
    if (!isPublishedDataset) {
      return null;
    }

    return {
      key: 'share',
      display: 'dropdown',
      getDropdownItem: () => ({
        label: t('studio.table.actions.shareInPost'),
        biData: BiData<ClickSocialShareBiEvent>({
          evid: 356,
          typeid: getDeviationResourceType(deviation),
          itemid: deviation.deviationId,
          sectionname: BiContextProvidedValue,
          social_share: 'da_post',
        }),
        onClick: () => {
          dispatchShareDeviationToPost(deviation);
        },
      }),
      dropdownGroupIndex: 1,
      dropdownOrder: 1,
    };
  }, [t, deviation, dispatchShareDeviationToPost, isPublishedDataset]);
}

function useDreamupEditTableAction(deviation: PapiDeviation): TableAction | null {
  const dreamupEditAction = useDreamupEditAction(deviation);
  return useMemo(() => {
    if (!dreamupEditAction) {
      return null;
    }

    return {
      key: 'dreamupEdit',
      display: 'dropdown',
      getDropdownItem: () => dreamupEditAction,
      dropdownGroupIndex: 2,
      dropdownOrder: 2,
    };
  }, [dreamupEditAction]);
}

function useScheduleAction(deviation: PapiDeviation): TableAction | null {
  const { t } = useTranslation();
  const dispatchPushModal = useAction(pushModal);
  const onScheduleClick = useCallback(
    () =>
      dispatchPushModal(ModalType.DEVIATION_SUBMIT_SCHEDULE, {
        params: {
          deviation,
          scheduleTimestamp: deviation.scheduledPublishTime,
        },
      }),
    [deviation, dispatchPushModal]
  );

  const isUnpublishedDataset = useSelector(getIsUnpublishedDataset);

  return useMemo(() => {
    if (!isUnpublishedDataset) {
      return null;
    }

    const isScheduled = !deviation.isPublished && deviation.scheduledPublishTime;
    const preview = DeviationMediaService.getDeviationFile(deviation, 'preview');
    const isMissingPreview = preview?.src.includes('minish/bears/empty');

    return {
      key: 'schedule',
      display: 'dropdown',
      getDropdownItem: () => ({
        label: isScheduled
          ? t('studio.table.actions.reschedule')
          : t('studio.table.actions.schedule'),
        onClick: onScheduleClick,
        disabled: isMissingPreview,
        biData: BiData<ScheduleOpenClickBiEvent>({
          evid: 36,
          sectionname: BiContextProvidedValue,
          typeid: getDeviationResourceType(deviation),
          itemid: deviation.deviationId,
          action: isScheduled ? 'reschedule' : 'schedule',
        }),
      }),
      dropdownGroupIndex: 2,
      dropdownOrder: 1,
    };
  }, [t, deviation, onScheduleClick, isUnpublishedDataset]);
}

function useUnpublishAction(deviation: PapiDeviation): TableAction | null {
  const { t } = useTranslation();
  const initiateUnpublishFlow = useUnpublishFlow({
    deviation,
    onCompleteAction: OnUnpublishCompleteAction.Toast,
    confirmBtnBiData: BiData<UnlistItemConfirmBiEvent>({
      evid: 359,
      itemid: deviation.deviationId,
      typeid: getDeviationResourceType(deviation),
      sectionname: BiContextProvidedValue,
    }),
  });

  const activeSection = useSelector(getActiveSection);

  const isUnpublishActionEnabled = [
    StudioSection.PUBLISHED,
    StudioSection.PRODUCTS_EXCLUSIVE,
    StudioSection.PRODUCTS_PCP,
    StudioSection.APPS,
  ].includes(activeSection);

  return useMemo(() => {
    if (!isUnpublishActionEnabled) {
      return null;
    }

    return {
      key: 'unpublish',
      display: 'dropdown',
      getDropdownItem: () => ({
        label: t('studio.table.actions.unpublish'),
        onClick: initiateUnpublishFlow,
        biData: BiData<UnlistItemOpenBiEvent>({
          evid: 358,
          sectionname: BiContextProvidedValue,
          typeid: getDeviationResourceType(deviation),
          itemid: deviation.deviationId,
        }),
      }),
      dropdownGroupIndex: 2,
      dropdownOrder: 0,
    };
  }, [t, initiateUnpublishFlow, isUnpublishActionEnabled, deviation]);
}

export function useSellAction(deviation: PapiDeviation): TableAction | null {
  const dropdownSaleAction = useSaleActions(deviation);
  const activeSection = useSelector(getActiveSection);
  const { contentType } = useSelector(getStudioParams);

  return useMemo(() => {
    if (activeSection === StudioSection.PUBLISHED && contentType === 'posts') {
      return null;
    } else if (
      ![
        StudioSection.DRAFTS,
        StudioSection.SCHEDULED,
        StudioSection.PLAYGROUND,
        StudioSection.PUBLISHED,
        StudioSection.STASH,
        StudioSection.PRODUCTS_EXCLUSIVE,
        StudioSection.PRODUCTS_EXCLUSIVE_UNLISTED,
        StudioSection.PRODUCTS_PCP,
        StudioSection.PRODUCTS_PCP_UNLISTED,
      ].includes(activeSection)
    ) {
      return null;
    }

    return {
      key: 'sell',
      display: 'mainCta-or-dropdown',
      renderButton: () => (
        <SellButton
          key="sell-button"
          deviation={deviation}
          biData={BiData<SellYourArtClickBiEvent>({
            evid: 353,
            deviationid: deviation.deviationId,
            sectionname: BiContextProvidedValue,
          })}
        />
      ),
      getDropdownItem: () => dropdownSaleAction,
      dropdownGroupIndex: 2,
      dropdownOrder: 0,
    };
  }, [deviation, dropdownSaleAction, activeSection, contentType]);
}

function usePublishTierAction(deviation: PapiDeviation): TableAction | null {
  const { t } = useTranslation();
  const { onEditClick } = useEditDeviationClick(deviation);

  return useMemo(() => {
    if (!deviation.tier) {
      return null;
    }

    return {
      key: 'publishTier',
      display: 'dropdown',
      getDropdownItem: () => ({
        label: t('studio.table.actions.publishTier'),
        onClick: onEditClick,
      }),
      dropdownGroupIndex: 1,
      dropdownOrder: 1,
    };
  }, [t, deviation, onEditClick]);
}

function useOriginalStatusAction(deviation: PapiDeviation): TableAction | null {
  const { t } = useTranslation();
  const activeSection = useSelector(getActiveSection);
  const dispatchChangeOriginalStatus = useAction(changeOriginalStatus);
  const dispatchAddNotification = useAction(addNotification);

  // Check if original is currently open (default to true if not set)
  const isOpen = (deviation.originals as any)?.isOpen !== false;

  const onToggleStatus = useCallback(() => {
    const newStatus = isOpen ? OriginalStatusType.CLOSED : OriginalStatusType.OPEN;
    dispatchChangeOriginalStatus({
      deviation,
      status: newStatus,
    });
    // Show toast notification when triggered from three-dots menu
    dispatchAddNotification(NotificationType.Generic, {
      message: isOpen
        ? t('studio.products.original.closedNotification')
        : t('studio.products.original.openedNotification'),
    });
  }, [dispatchChangeOriginalStatus, dispatchAddNotification, deviation, isOpen, t]);

  return useMemo(() => {
    if (
      activeSection !== StudioSection.PRODUCTS_ORIGINAL &&
      activeSection !== StudioSection.PRODUCTS_ORIGINAL_UNLISTED
    ) {
      return null;
    }

    return {
      key: 'originalStatus',
      display: 'mainCta-or-dropdown',
      renderButton: () => <OriginalStatusEdit key="original-status" deviation={deviation} />,
      getDropdownItem: () => ({
        label: isOpen
          ? t('studio.products.original.closeOriginal')
          : t('studio.products.original.openOriginal'),
        onClick: onToggleStatus,
      }),
      dropdownGroupIndex: 1,
      dropdownOrder: 0,
    };
  }, [t, deviation, activeSection, isOpen, onToggleStatus]);
}
